import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, IconButton, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import GooglePlacesAutocomplete from '../../../../commons/formComponents/GooglePlacesAutocomplete';
import GooglePlacesMap from '../../../../commons/googleMaps/googlePlacesMap';
import { createAgencia, updateAgencia, fetchPrestadoresTraslado } from '../../../../../actions/traslados';
import { getArtPrestadores } from '../../../../../actions/arts';
import snackbarUtils from '../../../../../utils/snackbarUtils';
import { locationFromCoords } from '../../../forms/fptTraslados/fptTrasladosUtils';

const emptyForm = {
    razonSocial: '',
    idPrestador: '',
    telefono: '',
    mail: '',
    direccion: '',
    latitud: null,
    longitud: null,
    observaciones: ''
};

const AgenciaFormDialog = ({ open, onClose, agencia }) => {
    const dispatch = useDispatch();
    const artPrestadores = useSelector((state) => state.arts.artPrestadores);
    const idArt = useSelector((state) =>
        state.auth.artAsociada?.id ||
        state.formsState.commonProperties?.idArt ||
        artPrestadores?.data?.[0]?.arts?.[0]?.id ||
        state.arts.list?.[0]?.id
    );

    const isEdit = !!agencia;

    const [formData, setFormData] = useState(emptyForm);
    const [prestadores, setPrestadores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mapQuery, setMapQuery] = useState('');

    useEffect(() => {
        if (!artPrestadores?.data?.length && !artPrestadores?.isFetching) {
            dispatch(getArtPrestadores());
        }
    }, [dispatch, artPrestadores?.data?.length, artPrestadores?.isFetching]);

    useEffect(() => {
        if (open) {
            dispatch(fetchPrestadoresTraslado())
                .then((res) => {
                    setPrestadores(Array.isArray(res) ? res : []);
                })
                .catch(() => setPrestadores([]));
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (agencia) {
            setFormData({
                razonSocial: agencia.razonSocial || '',
                idPrestador: agencia.idPrestador != null ? String(agencia.idPrestador) : '',
                telefono: agencia.telefono || '',
                mail: agencia.mail || '',
                direccion: agencia.direccion || '',
                latitud: agencia.latitud ?? null,
                longitud: agencia.longitud ?? null,
                observaciones: agencia.observaciones || ''
            });
        } else {
            setFormData(emptyForm);
        }
    }, [agencia, open]);

    // El iframe del mapa se recarga al cambiar la query, así que espera a que termine de escribir.
    useEffect(() => {
        const timeoutId = window.setTimeout(() => setMapQuery(formData.direccion), 700);
        return () => window.clearTimeout(timeoutId);
    }, [formData.direccion]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDireccionChange = (value) => {
        setFormData(prev => ({
            ...prev,
            direccion: value,
            latitud: null,
            longitud: null
        }));
    };

    const handlePlaceSelected = (address) => {
        setFormData(prev => ({
            ...prev,
            direccion: address?.formattedAddress || prev.direccion,
            latitud: address?.location?.lat ?? null,
            longitud: address?.location?.lng ?? null
        }));
    };

    const handleSubmit = () => {
        if (!formData.razonSocial.trim()) {
            snackbarUtils.error("La Razón Social es obligatoria");
            return;
        }

        const location = locationFromCoords(formData.latitud, formData.longitud);
        const payload = {
            ...formData,
            idPrestador: formData.idPrestador ? parseInt(formData.idPrestador, 10) : null,
            latitud: location?.lat ?? null,
            longitud: location?.lng ?? null
        };

        setLoading(true);
        const action = isEdit
            ? updateAgencia(agencia.idAgencia, payload)
            : createAgencia(payload);

        dispatch(action)
            .then(() => {
                snackbarUtils.success(`Agencia ${isEdit ? 'actualizada' : 'creada'} correctamente`);
                onClose();
            })
            .catch(() => {
                snackbarUtils.error(`Error al ${isEdit ? 'actualizar' : 'crear'} la agencia`);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const location = locationFromCoords(formData.latitud, formData.longitud);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ backgroundColor: "#5b9bf5", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1">{isEdit ? 'EDITAR AGENCIA' : 'NUEVA AGENCIA'}</Typography>
                <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        required
                        label="Razón Social"
                        name="razonSocial"
                        value={formData.razonSocial}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        select
                        label="Prestador de traslado"
                        name="idPrestador"
                        value={formData.idPrestador}
                        onChange={handleChange}
                        fullWidth
                        helperText="Prestador al que responde la agencia (opcional)"
                    >
                        <MenuItem value="">
                            <em>Ninguno</em>
                        </MenuItem>
                        {formData.idPrestador && !prestadores?.some(p => String(p.idPrestador) === String(formData.idPrestador)) && agencia?.prestadorNombre && (
                            <MenuItem key={formData.idPrestador} value={String(formData.idPrestador)}>
                                {agencia.prestadorNombre}
                            </MenuItem>
                        )}
                        {(prestadores || []).map((p) => (
                            <MenuItem key={p.idPrestador} value={String(p.idPrestador)}>
                                {p.razonSocial}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Teléfono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Mail"
                        name="mail"
                        placeholder="ejemplo@mail.com, otro@mail.com"
                        value={formData.mail}
                        onChange={handleChange}
                        fullWidth
                        helperText="Admite múltiples direcciones separadas por coma"
                    />
                    <GooglePlacesAutocomplete
                        autoComplete="off"
                        freeSolo
                        fullWidth
                        handleSubmit={handlePlaceSelected}
                        idArt={idArt}
                        initialInputValue={agencia?.direccion || ''}
                        label="Dirección"
                        onInputValueChange={handleDireccionChange}
                        placeholder="Buscar dirección..."
                        variant="outlined"
                    />
                    <GooglePlacesMap
                        idArt={idArt}
                        showSearch={false}
                        searches={[
                            {
                                key: 'agencia',
                                title: 'Dirección de la agencia',
                                location,
                                query: location ? '' : mapQuery
                            }
                        ]}
                    />
                    <TextField
                        label="Observaciones"
                        name="observaciones"
                        value={formData.observaciones}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="primary" disabled={loading}>CANCELAR</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
                    GUARDAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AgenciaFormDialog;
